/**
 * Cobrar una factura: entera o a plazos.
 *
 * Vive suelto y sin importar nada, como el resto de `lib/`: la pantalla
 * importa `@/App` y el router, y esto son cuentas.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 24-sep-2026: "al pasar una factura a pagada no me deja decir que es
 * por pago parcial... que han pagado una parte y queda otra por pagar, y esto
 * nos ocurre con frecuencia".
 *
 * Réplica de `pagos_factura.py`, redondeos incluidos: si la pantalla dijera
 * que quedan 180 € y el servidor guardara otra cosa, nadie volvería a fiarse
 * de la pantalla. Misma razón que en `impuestos.js`.
 */

/** Medio céntimo: por debajo de eso está cobrada. Igual que el servidor. */
const TOLERANCIA = 0.005;

/**
 * Un importe tecleado, como se teclea AQUÍ.
 *
 * En España los decimales van con coma, y `Number("54,33")` es `NaN`. Eso
 * dejaba el importe en 0 y el botón de apuntar el cobro no se habilitaba
 * nunca: saltaba "el importe tiene que ser mayor que 0 €" sobre un campo
 * donde se veía escrito 54,33 (Angel, 7-oct-2026).
 *
 * Si hay coma, los puntos son separadores de millar: "1.234,56" es mil
 * doscientos treinta y cuatro con cincuenta y seis, no un número roto. Sin
 * coma, el punto es el decimal de toda la vida.
 */
export function dinero(valor) {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? Math.round(valor * 100) / 100 : 0;
  }
  if (typeof valor !== 'string') return 0;

  let limpio = valor.trim().replace(/\s/g, '');
  if (limpio.includes(',')) {
    limpio = limpio.replace(/\./g, '').replace(',', '.');
  }
  if (limpio === '') return 0;

  const n = Number(limpio);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

/** Lo que se lleva cobrado, sumando los pagos guardados. */
export function totalCobrado(pagos) {
  // Un cobro que el banco ha devuelto se queda en la lista, pero no cuenta.
  const vigentes = (pagos || []).filter((p) => !p?.devuelto_en);
  return dinero(vigentes.reduce((suma, p) => suma + dinero(p?.importe), 0));
}

/**
 * Lo que falta por saldar, SIEMPRE en positivo.
 *
 * En MAGNITUD, no restando tal cual. Un abono tiene el total en negativo
 * —son 60,38 € que SALEN— y restando a secas lo pendiente daba
 * `Math.max(0, -60.38)` = 0: el diálogo decía "Entera (0,00 €)" sobre un
 * abono de 60 € y el botón no se habilitaba nunca (Angel, 7-oct-2026).
 *
 * Y en magnitud da igual con qué signo se guardara lo ya pagado: las
 * facturas lo guardan en positivo y los abonos en negativo, y mezclar los
 * dos criterios es de donde salen los descuadres.
 *
 * Nunca negativo: un cliente que paga de más no queda a favor aquí.
 */
export function pendiente(total, cobrado) {
  const queda = Math.abs(dinero(total)) - Math.abs(dinero(cobrado));
  return Math.max(0, dinero(queda));
}

/** Cómo va el cobro de esta factura, para pintarlo. */
export function resumenDeCobro(factura) {
  const f = factura || {};
  const total = dinero(f.total);
  // `amount_paid` es la suma que guarda el servidor; los pagos son el respaldo
  // para una factura que venga de antes de que existiera el campo.
  const cobrado = f.amount_paid !== undefined && f.amount_paid !== null
    ? dinero(f.amount_paid) : totalCobrado(f.pagos);
  const falta = pendiente(total, cobrado);
  return { total, cobrado, pendiente: falta, completa: falta < TOLERANCIA };
}

/**
 * Si a esta factura se le puede ofrecer el cobro a plazos.
 *
 * Angel: "las facturas recurrentes o las que se pasan por sepa siempre son
 * enteras". Así que ahí no se pregunta: el banco gira el recibo entero o no lo
 * gira. Preguntarlo en esos casos sería ofrecer una opción que no pasa nunca,
 * en el camino que más se usa.
 *
 * Es una decisión de PANTALLA, no una regla del sistema: el servidor acepta un
 * cobro parcial en cualquier factura. Hace falta que sea así porque un recibo
 * SEPA devuelto se puede acabar recuperando a plazos, y entonces sí.
 */
export function admitePagoParcial(factura) {
  const f = factura || {};
  if (f.is_recurring) return false;
  // En una remesa: la gira el banco, entera.
  if (f.sepa_remittance_id && f.status !== 'recibo_devuelto') return false;
  if (f.payment_method === 'sepa' && f.status !== 'recibo_devuelto') return false;
  return true;
}

/**
 * Por qué todavía no se puede apuntar el cobro. Null cuando sí se puede.
 *
 * No repite las reglas del servidor: se adelanta a las que cuestan una vuelta
 * entera. Quien decide sigue siendo el servidor.
 */
export function motivoParaNoCobrar(formulario, factura) {
  const f = formulario || {};
  if (!f.fecha) return 'Falta la fecha del cobro.';
  if (!f.parcial) return null;

  const importe = dinero(f.importe);
  if (importe <= 0) return 'El importe tiene que ser mayor que 0 €.';
  const falta = resumenDeCobro(factura).pendiente;
  if (importe > falta + 0.01) {
    // Un abono se devuelve, no se cobra. Decir "por cobrar" sobre un abono
    // hace dudar de si la pantalla ha entendido qué documento es.
    const que = esAbono(factura) ? 'devolver' : 'cobrar';
    return `Solo quedan ${falta.toFixed(2)} € por ${que}.`;
  }
  return null;
}

/**
 * Si este documento es un abono: el total en negativo.
 *
 * Lo decide el importe y no `is_return`, porque es el importe el que manda
 * en todas las cuentas de este fichero. Un documento marcado abono pero con
 * total positivo se cobra, diga lo que diga la marca.
 */
export function esAbono(factura) {
  return dinero((factura || {}).total) < 0;
}

/**
 * Si este documento no tiene nada que cobrar: total 0.
 *
 * Suele ser una factura que el saldo a favor del cliente ya ha cubierto. No se
 * apunta un cobro de 0: se salda.
 */
export function sinImporte(factura) {
  return dinero((factura || {}).total) === 0;
}

/**
 * Lo que puede salir en la lista para girar una remesa.
 *
 * Sin las que ya están en otra y sin las de 0: el banco rechaza un adeudo de
 * cero y Myriam no quiere verlas en la lista (31-oct-2026).
 */
export function recibosDisponibles(facturas) {
  return (facturas || []).filter((f) => !f.sepa_remittance_id && !sinImporte(f));
}

/** El cuerpo del POST /invoices/{id}/cobro. */
export function cuerpoDeCobro(formulario) {
  const f = formulario || {};
  return {
    // Sin importe el servidor cobra lo que falte, que es "la ha pagado
    // entera". Mandar el total calculado aquí permitiría que pantalla y
    // servidor discreparan por un céntimo.
    importe: f.parcial ? dinero(f.importe) : null,
    fecha: f.fecha || null,
    metodo: f.metodo || null,
    nota: (f.nota || '').trim() || null,
  };
}

export default resumenDeCobro;
